import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getScoreTextColor, scoreBarToneClass } from '@/components/optimization-score/score-colors';
import type { WorkbenchComponentScores, WorkbenchProduct } from '@/hooks/useWorkbench';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { HeroDonut } from '@/components/optimization-score/HeroDonut';
import { weakestComponent } from '@/lib/scoreWeakest';
import { componentTouchState, gpcEpochComparable, needsTouchText, type TouchState } from '@/lib/scoreTouch';
import { CollapsibleField } from './CollapsibleField';

type TFunc = ReturnType<typeof useTranslation>['t'];

/**
 * Die sechs Score-Komponenten in Anzeigereihenfolge, mit ihrem Gewicht.
 *
 * Quelle der Gewichte ist `SCORING_WEIGHTS` in
 * `supabase/functions/_shared/scoring.ts` (title 25, description 20,
 * details 15, highlights 10, gpc 10, attribute 20).
 *
 * Reihenfolge STRENG GEWICHTSABSTEIGEND (25, 20, 20, 15, 10, 10), so wie das
 * Mockup sie zeichnet. Vorher stand hier die Folge des
 * Feed-Check-Aufklappers - was schon damals nicht stimmte, denn der sortiert
 * dynamisch nach Punkteverlust. Die Folge sichtbar zu machen kostet nichts
 * und traegt: jede Zeile zeigt ihr Gewicht mit an, und eine Spalte
 * 25/20/10/15/10/20 liest sich wie ein Fehler.
 *
 * Aendert sich dort ein Gewicht, muss es hier mitziehen; der Summentest
 * faengt zumindest jede Aenderung ab, die die 100 verlaesst.
 */
export const SCORE_COMPONENTS: ReadonlyArray<{
  key: keyof WorkbenchComponentScores;
  weightPct: number;
}> = [
  { key: 'title', weightPct: 25 },
  { key: 'description', weightPct: 20 },
  { key: 'attributes', weightPct: 20 },
  { key: 'details', weightPct: 15 },
  { key: 'gpc', weightPct: 10 },
  { key: 'highlights', weightPct: 10 },
];

/**
 * Die einzeilige Fassung der Punkte (`punkteTiefe: 'eineZeile'`).
 *
 * Lauter "±0" untereinander ist der Normalfall und sagt nichts - deshalb
 * gibt es HIER kein Aufklappen, wenn sich an keiner der gewerteten Komponenten
 * etwas geaendert hat: ein Knopf ohne neue Information ist ein Knopf zu
 * viel. Erst ein tatsaechliches Delta macht die Diagnose sehenswert, und nur
 * dann ist die Zeile ein Aufklapper (kein Knopf, kein `aria-expanded`, kein
 * Chevron im Ruhezustand sonst).
 */
function EineZeileScore({
  t,
  zeile,
  hatAbweichung,
  zeilen,
  anzahlBereiche,
}: {
  t: TFunc;
  zeile: string;
  hatAbweichung: boolean;
  zeilen: ReactNode;
  /** Wieviele Bereiche gewertet werden, fuer die Fussnote. 5 oder 6. */
  anzahlBereiche: number;
}) {
  const [offen, setOffen] = useState(false);

  if (!hatAbweichung) {
    return (
      <p data-testid="score-oneline" className="text-sm font-semibold text-foreground">
        {zeile}
      </p>
    );
  }

  return (
    <div>
      <button
        type="button"
        data-testid="score-oneline-toggle"
        aria-expanded={offen}
        aria-label={t('score.detailsAria')}
        onClick={() => setOffen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-md text-left transition-colors hover:bg-muted/40"
      >
        <span data-testid="score-oneline" className="text-sm font-semibold text-foreground">
          {zeile}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${offen ? 'rotate-180' : ''}`}
        />
      </button>
      {offen && (
        <div className="mt-2 space-y-2">
          {zeilen}
          <p className="text-[10px] leading-tight text-muted-foreground">
            {t('score.totalNote', { anzahl: anzahlBereiche })}
          </p>
        </div>
      )}
    </div>
  );
}

interface ScorePanelProps {
  post: WorkbenchComponentScores | null;
  postTotal: number | null;
  pre: WorkbenchComponentScores | null;
  preTotal: number | null;
  scoreType: WorkbenchProduct['scoreType'];
  /**
   * Score v3: fuehrt die Kategorie dieses Produkts ueberhaupt Attribute?
   *
   * `false` heisst, die Edge-Seite hat den Attribut-Bucket weggelassen und die
   * uebrigen fuenf auf ihre eigene Gewichtssumme renormiert
   * (`_shared/scoring.ts:normFor`). Der gespeicherte `attribute_score` ist dann
   * eine 0 ohne Aussage -- als Balken gerendert liest sie sich wie "null von
   * hundert", und als schwaechster Bereich schickt sie den Nutzer an eine
   * Stelle, an der es nichts zu tun gibt.
   *
   * Vorgabe `true`, dieselbe wie die Datenbank-Spalte (`NOT NULL DEFAULT
   * true`): eine fehlende Angabe heisst "wie bisher", nicht "gibt es nicht".
   * Der umgekehrte Rueckfall verschwiege den Bereich auf jeder Karte, deren
   * Score-Zeile die Spalte nicht geladen hat.
   */
  attributeApplicable?: boolean;
  /**
   * Score v3.1: Rechenzeitpunkt der pre- bzw. post-Score-Zeile
   * (`product_scores.calculated_at`, durchgereicht von `useWorkbench`).
   *
   * Sie entscheiden ueber GENAU EINE Aussage: ob die GPC-Zeile ihren
   * Vorher/Nachher-Zustand zeigen darf. Der GPC-Score war bis v3.0 binaer (100
   * fuer jede gesetzte Kategorie) und zaehlt seit v3.1 die Tiefe (40 / 70 /
   * 100). Eine alte pre-Zeile gegen eine neue post-Zeile gehalten meldet einen
   * Rueckgang von 100 auf 40, ohne dass sich an der Kategorie etwas geaendert
   * haette -- die Karte behauptete dann einen Schaden, den die Optimierung nie
   * angerichtet hat, und zwar bei JEDEM Bestandsprodukt.
   *
   * `product_scores` traegt keine `scoring_version`; der Rechenzeitpunkt ist
   * das einzige Epochen-Merkmal, das eine Zeile mitbringt (Begruendung und
   * Grenze in `scoreTouch.ts:GPC_DEPTH_SCORING_SINCE`).
   *
   * Weglassen heisst "nicht bekannt" und damit "nicht vergleichbar": die
   * GPC-Zeile schweigt dann, statt zu raten. Die uebrigen fuenf Komponenten
   * sind davon unberuehrt -- ihre Rechnung hat sich mit v3.1 nicht geaendert.
   */
  preCalculatedAt?: string | null;
  postCalculatedAt?: string | null;
}

/**
 * Die Null ist kein Schmuck, sondern eine eigene Aussage: "geprueft, nichts
 * passiert". Ohne sie liest sich eine fehlende Zahl wie ein fehlender Wert
 * und ein "+0" wie ein Gewinn.
 */
function formatDelta(delta: number): string {
  if (delta === 0) return '±0';
  return delta > 0 ? `+${delta}` : `${delta}`;
}

function deltaToneClass(delta: number): string {
  if (delta > 0) return 'text-success-ink';
  // Beide Richtungen auf ihrer Tinte: das rohe destructive-Token faellt mit
  // 3.78:1 durch AA, destructive-ink haelt 6.42:1.
  if (delta < 0) return 'text-destructive-ink';
  return 'text-muted-foreground';
}

/**
 * Die Punkte, immer sichtbar.
 *
 * Sie ersetzt drei fruehere Anzeigen: den Score-Badge im Kartenkopf, die
 * Wirkungszeile ueber den Spalten und den aufklappbaren Score-Aufbau unter
 * ihnen. Dieselbe Zahl stand damit dreimal auf einer Karte, an einer davon
 * nur nach einem Klick.
 */
export function ScorePanel({
  post, postTotal, pre, preTotal, scoreType, attributeApplicable = true,
  preCalculatedAt, postCalculatedAt,
}: ScorePanelProps) {
  const { t } = useTranslation('workbench');
  // Vor dem fruehen Return gelesen: ein Hook darf nicht bedingt sein, und der
  // leere Zustand (kein Score) kehrt weiter unten frueher zurueck.
  const { punkteTiefe, punkteAnzeige } = useEntwurf();
  /**
   * Die Komponenten, die fuer DIESES Produkt ueberhaupt zaehlen.
   *
   * Gefiltert wird hier am Aufrufer und NICHT in `SCORE_COMPONENTS`:
   * `scoreWeakest.ts` verlaesst sich darauf, dass die uebergebene Liste streng
   * gewichtsabsteigend ist (dort ausgeschrieben, `scoreWeakest.test.ts` pinnt
   * es). Ein `filter` erhaelt die Reihenfolge, ein Umsortieren der Konstanten
   * braeche die Determinismus-Zusage still.
   */
  const aktiveKomponenten = useMemo(
    () => (attributeApplicable ? SCORE_COMPONENTS : SCORE_COMPONENTS.filter((c) => c.key !== 'attributes')),
    [attributeApplicable],
  );

  /**
   * Wieviele Bereiche diese Karte wirklich bewertet. Drei Texte nennen die
   * Zahl (`score.allStrong`, `score.detailsToggle`, `score.totalNote`); vor
   * Score v3 stand dort fest "sechs", und das war ab dem ersten Produkt ohne
   * Attribut-Bucket schlicht falsch.
   *
   * Die Interpolation heisst `anzahl` und NICHT `count`: i18next behandelt
   * `count` als Plural-Schluessel und suchte dann nach `_one`/`_other`. Der
   * Wert ist hier immer 5 oder 6, nie 1 -- vier zusaetzliche Schluessel je
   * Sprache fuer eine Mehrzahl, die nie eintritt, waeren Ballast. Deutsche
   * Variablennamen sind in dieser Datei das Hausmuster (`score.weakest`
   * traegt `bereich` und `wert`).
   */
  const anzahlBereiche = aktiveKomponenten.length;
  // Perf-Minor aus dem Karten-Abschlussbericht, abgearbeitet zur
  // Befoerderung: vorher lief die Suche bei jedem Render, auch wenn sich
  // an den Scores nichts geaendert hat.
  const schwaechster = useMemo(
    () => (post ? weakestComponent(post, aktiveKomponenten) : null),
    [post, aktiveKomponenten],
  );

  if (!post || postTotal === null) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('score.heading')}
        </h3>
        <p data-testid="score-panel-none" className="text-sm text-muted-foreground">
          {t('score.none')}
        </p>
      </div>
    );
  }

  const roundedPost = Math.round(postTotal);
  const roundedPre = preTotal !== null ? Math.round(preTotal) : null;
  const totalDelta = roundedPre !== null ? roundedPost - roundedPre : null;

  /* "Delta ungleich 0" heisst: `pre` liegt vor UND mindestens eine der
     AKTIVEN Komponenten weicht ab. Fehlt `pre`, gibt es keine Deltas - dann
     ebenfalls kein Aufklapper, die Zeile zeigt nur den Ist-Wert.
     Ein Delta auf einem Bucket, den dieses Produkt gar nicht fuehrt, ist
     keine Aenderung: der Knopf oeffnete sonst eine Liste, in der keine
     einzige Zeile eine Abweichung zeigt. */
  const hatAbweichung = pre !== null && aktiveKomponenten.some(({ key }) => post[key] !== pre[key]);

  // Spec 2026-09-15: der Zustand des schwaechsten Bereichs entscheidet, ob der
  // Kopf (und die eine Zeile) den Zusatz "nicht angefasst" tragen. Nur fuer
  // post-Scores mit Vorher-Zeile; ein pre-Score hat keine Optimierung, die
  // etwas angefasst haben koennte.
  //
  // Score v3.1: die GPC-Zeile bekommt zusaetzlich den Epochen-Guard. Sie ist
  // die einzige Komponente, deren Rechnung sich zwischen zwei gespeicherten
  // Zeilen geaendert haben kann (binaer bis v3.0, Tiefe ab v3.1), und
  // `product_scores` haelt nicht fest, welche Fassung eine Zeile gerechnet hat
  // -- siehe `preCalculatedAt` oben.
  const zustandFuer = (key: keyof WorkbenchComponentScores): TouchState => {
    if (scoreType !== 'post_optimization' || !post) return 'unknown';
    if (key === 'gpc' && !gpcEpochComparable(preCalculatedAt, postCalculatedAt)) return 'unknown';
    return componentTouchState(pre, post, key, attributeApplicable);
  };
  const schwaechsterNichtAngefasst = schwaechster !== null && zustandFuer(schwaechster.key) === 'unchanged_low';

  const eineZeileTextBasis =
    roundedPre !== null
      ? t('score.oneLine', { pre: roundedPre, post: roundedPost })
      : t('score.oneLineNoPre', { post: roundedPost });

  const eineZeileText =
    schwaechsterNichtAngefasst && schwaechster
      ? t('score.oneLineUnchangedLow', {
          zeile: eineZeileTextBasis,
          bereich: t(`score.components.${schwaechster.key}`),
        })
      : eineZeileTextBasis;

  const befund = schwaechster
    ? t(schwaechsterNichtAngefasst ? 'score.weakestUnchangedLow' : 'score.weakest', {
        bereich: t(`score.components.${schwaechster.key}`),
        wert: schwaechster.value,
      })
    : t('score.allStrong', { anzahl: anzahlBereiche });

  const zeilen = (
    <div className="space-y-2">
      {SCORE_COMPONENTS.map(({ key, weightPct }) => {
        const value = post[key];
        const delta = pre ? value - pre[key] : null;
        const zustand = zustandFuer(key);

        /* Score v3: die Zeile bleibt stehen, aber ohne Zahl, ohne Gewicht und
           ohne Balken. Alle drei waeren Falschaussagen -- der gespeicherte
           Wert ist eine 0 ohne Bedeutung, das Gewicht faellt edge-seitig aus
           dem Nenner, und ein Balken der Laenge 0 saehe aus wie "null von
           hundert". Weglassen kam nicht in Frage: dann faende der Leser die
           sechste Komponente nirgends wieder. */
        if (key === 'attributes' && !attributeApplicable) {
          return (
            <div data-testid={`score-row-${key}`} key={key} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="min-w-0 truncate text-foreground">
                  {t(`score.components.${key}`)}
                </span>
                <span
                  data-testid={`score-notapplicable-${key}`}
                  className="shrink-0 text-[10px] text-muted-foreground"
                >
                  {t('score.notApplicable')}
                </span>
              </div>
            </div>
          );
        }

        return (
          <div data-testid={`score-row-${key}`} key={key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate text-foreground">
                {t(`score.components.${key}`)}
              </span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className="text-[10px] text-muted-foreground">{`${weightPct}%`}</span>
                {delta === null ? (
                  <span
                    data-testid={`score-value-${key}`}
                    className="font-semibold tabular-nums"
                    style={{ color: getScoreTextColor(value) }}
                  >
                    {value}
                  </span>
                ) : (
                  <span
                    data-testid={`score-delta-${key}`}
                    className={cn('font-semibold tabular-nums', deltaToneClass(delta))}
                  >
                    {formatDelta(delta)}
                  </span>
                )}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
              <div
                data-testid={`score-bar-${key}`}
                className={`h-full rounded-full ${scoreBarToneClass(value)}`}
                style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
              />
            </div>
            {needsTouchText(zustand) && (
              <p
                data-testid={`score-touch-${key}`}
                className="text-[10px] leading-tight text-muted-foreground"
                title={zustand === 'unchanged_low' ? t('score.touch.tooltip') : undefined}
              >
                {t(zustand === 'worse' ? 'score.touch.worse' : 'score.touch.unchangedLow')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3">
      {punkteTiefe === 'eineZeile' ? (
        /* Die Zeile beschriftet sich selbst - `score.heading` entfaellt
           hier, siehe Spec 5.2: EINE Zeile "Punkte 100 → 100" statt der
           Versalien-Ueberschrift plus separater Zahl. */
        <EineZeileScore
            t={t}
            zeile={eineZeileText}
            hatAbweichung={hatAbweichung}
            zeilen={zeilen}
            anzahlBereiche={anzahlBereiche}
          />
      ) : (
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('score.heading')}
          </h3>
          <span
            data-testid="score-panel-total"
            className={cn(
              'flex gap-1.5 tabular-nums',
              punkteAnzeige === 'ring' ? 'items-center' : 'items-baseline',
            )}
          >
            {roundedPre !== null && (
              <>
                <span className="text-sm text-muted-foreground">{roundedPre}</span>
                <ArrowRight className="h-3 w-3 self-center text-muted-foreground" aria-hidden="true" />
              </>
            )}
            {punkteAnzeige === 'ring' ? (
              /* Derselbe Donut wie im Feed-Check, verkleinert: Wiedererkennung
                 statt dritter Darstellungsform. Die "von 100"-Unterzeile ist
                 bei 72 px unlesbar und deshalb aus (subLabel, opt-in am
                 HeroDonut). Vorher-Wert und Delta bleiben daneben stehen -
                 der Ring ersetzt die ZIFFER, nicht die Aussage. */
              <span data-testid="score-panel-ring" className="inline-flex">
                <HeroDonut score={roundedPost} size={72} subLabel={false} />
              </span>
            ) : (
              <span className="text-lg font-semibold" style={{ color: getScoreTextColor(roundedPost) }}>
                {roundedPost}
              </span>
            )}
            {totalDelta !== null && (
              <span
                data-testid="score-panel-delta"
                className={cn('text-sm font-semibold tabular-nums', deltaToneClass(totalDelta))}
              >
                {formatDelta(totalDelta)}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Die sechs Zahlen beschreiben dann das ORIGINAL und stehen trotzdem
          neben dem optimierten Text. Einmal am Kopf gesagt statt sechsmal an
          den Zeilen: es ist eine Eigenschaft des ganzen Score-Eintrags. */}
      {scoreType === 'pre_optimization' && (
        <p data-testid="score-panel-prenote" className="text-xs text-warning-ink">
          {t('score.preNote')}
        </p>
      )}

      {punkteTiefe === 'eineZeile' ? null : punkteTiefe === 'immerOffen' ? (
        <>
          {zeilen}
          {/* total_score ist base mal completeness_multiplier: wer die
              Gewichte nachrechnet, kommt auf eine andere Zahl. Ohne diesen
              Satz sieht die Abweichung wie ein Rechenfehler aus. */}
          <p className="text-[10px] leading-tight text-muted-foreground">
            {t('score.totalNote', { anzahl: anzahlBereiche })}
          </p>
        </>
      ) : (
        /* Der Befund steht im KOPF des geschlossenen Aufklappers. Ohne ihn
           muesste man klicken, um zu erfahren, ob der Klick lohnt - und genau
           das ist der Grund, aus dem `CollapsibleField` seine Zusammenfassung
           ueberhaupt hat. */
        <CollapsibleField
          label={t('score.detailsToggle', { anzahl: anzahlBereiche })}
          summary={befund}
          summaryTestId="score-weakest"
          fieldId="score"
        >
          {zeilen}
          <p className="mt-2 text-[10px] leading-tight text-muted-foreground">
            {t('score.totalNote', { anzahl: anzahlBereiche })}
          </p>
        </CollapsibleField>
      )}
    </div>
  );
}
